/**
 * DIN 5008 Form B Falz- and Lochmarken (Falthilfe) for DL window envelopes.
 * Positions are measured from the top paper edge; marks sit in the left Heftrand.
 */

import { mmToPt, type DocumentLayoutV1 } from './documentLayoutTypes';

export type DocumentFoldMarkKind = 'falz' | 'loch';

export type DocumentFoldMarkRect = {
  kind: DocumentFoldMarkKind;
  /** Left edge of the hairline from the paper left edge (pt). */
  xPt: number;
  /** Vertical center of the hairline from the paper top edge (pt). */
  yPt: number;
  /** Horizontal length of the hairline (pt). */
  wPt: number;
  /** Stroke thickness (pt). */
  hPt: number;
};

/** ~2 mm inset from the left edge — inside the 20 mm Heftrand, clear of a 25 mm writing margin. */
const FOLD_MARK_LEFT_MM = 2;
/** Falzmarken are typically ~5 mm; Lochmarke slightly longer. */
const FALZ_LENGTH_MM = 5;
const LOCH_LENGTH_MM = 8;
/** Hairline stroke; DIN does not prescribe thickness. */
export const DOCUMENT_FOLD_MARK_STROKE_PT = 0.5;
export const DOCUMENT_FOLD_MARK_COLOR = '#9ca3af';

/**
 * Form B (Briefkopf ≤ 45 mm): Falz at 105 / 210 mm, Lochmarke at 148.5 mm (A4 midline).
 * Matches the app's existing Form B address-window targeting.
 */
export const DIN5008_FORM_B_FOLD_MARKS_MM = [
  { kind: 'falz' as const, yMm: 105, lengthMm: FALZ_LENGTH_MM },
  { kind: 'loch' as const, yMm: 148.5, lengthMm: LOCH_LENGTH_MM },
  { kind: 'falz' as const, yMm: 210, lengthMm: FALZ_LENGTH_MM },
] as const;

export function layoutShowsFoldMarks(
  layout: Pick<DocumentLayoutV1, 'body'> | null | undefined,
): boolean {
  return layout?.body.foldMarks === true;
}

/** Absolute page coordinates for printing / previewing Form B fold marks. */
export function resolveDocumentFoldMarkRects(): DocumentFoldMarkRect[] {
  const xPt = Math.round(mmToPt(FOLD_MARK_LEFT_MM) * 10) / 10;
  return DIN5008_FORM_B_FOLD_MARKS_MM.map((mark) => {
    const wPt = Math.round(mmToPt(mark.lengthMm) * 10) / 10;
    const yCenterPt = Math.round(mmToPt(mark.yMm) * 10) / 10;
    return {
      kind: mark.kind,
      xPt,
      yPt: yCenterPt - DOCUMENT_FOLD_MARK_STROKE_PT / 2,
      wPt,
      hPt: DOCUMENT_FOLD_MARK_STROKE_PT,
    };
  });
}
